import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from '../../../components/DataTable';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { AdminStocksFilters } from './AdminStocksFilters';
import { getStockBadgeClassName, getStockStatus } from '../utils';

const AdminStocksTable = ({ providerOptions, serviceDeskOptions, stocks }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: 'serviceDeskName',
        header: 'Service Desk',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.serviceDeskName}</span>
            <span className="text-xs text-gray-500">ID: {row.original.serviceDeskId}</span>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.label || '',
        id: 'label',
        header: 'Élément',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.label}</span>
            <span className="text-xs text-gray-500">
              {row.original.objectKey || row.original.objectId || '-'}
            </span>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.brand || '',
        id: 'brand',
        header: 'Marque',
        enableSorting: true,
        cell: ({ row }) => (
          row.original.brand
            ? <Badge variant="outline" className="text-xs">{row.original.brand}</Badge>
            : <span className="text-sm text-gray-400">-</span>
        ),
      },
      {
        accessorFn: (row) => row.type || '',
        id: 'type',
        header: 'Type',
        enableSorting: true,
        cell: ({ row }) => (
          row.original.type
            ? <Badge variant="secondary" className="text-xs">{row.original.type}</Badge>
            : <span className="text-sm text-gray-400">-</span>
        ),
      },
      {
        accessorFn: (row) => row.stock,
        id: 'stock',
        header: 'Stock',
        enableSorting: true,
        cell: ({ row }) => (
          <Badge className={getStockBadgeClassName(row.original.stock)}>
            {row.original.stock}
          </Badge>
        ),
      },
      {
        accessorFn: (row) => row.providerLabel || '',
        id: 'provider',
        header: 'Source',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <Badge variant="outline">{row.original.providerLabel}</Badge>
        ),
      },
      {
        accessorFn: (row) => getStockStatus(row.stock),
        id: 'stockStatus',
        header: 'Statut du stock',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        meta: { hidden: true },
      },
    ],
    []
  );

  const renderCustomFilters = ({ table, columnFilters }) => (
    <AdminStocksFilters
      table={table}
      columnFilters={columnFilters}
      serviceDeskOptions={serviceDeskOptions}
      providerOptions={providerOptions}
    />
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Stocks disponibles</CardTitle>
        <CardDescription>
          Tableau triable, filtrable et exportable des éléments avec stock
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          data={stocks}
          columns={columns}
          emptyMessage="Aucun stock disponible"
          showGlobalSearch={true}
          showPagination={true}
          pageSize={50}
          customFilters={renderCustomFilters}
        />
      </CardContent>
    </Card>
  );
};

const stockShape = PropTypes.shape({
  brand: PropTypes.string,
  label: PropTypes.string.isRequired,
  objectId: PropTypes.string,
  objectKey: PropTypes.string,
  provider: PropTypes.string.isRequired,
  providerLabel: PropTypes.string.isRequired,
  serviceDeskId: PropTypes.number.isRequired,
  serviceDeskName: PropTypes.string.isRequired,
  stock: PropTypes.number.isRequired,
  type: PropTypes.string,
});

AdminStocksTable.propTypes = {
  providerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  serviceDeskOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  stocks: PropTypes.arrayOf(stockShape).isRequired,
};

export { AdminStocksTable };
